import React from 'react'
import { Editor } from '@tiptap/react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  Link2,
  Link2Off,
  Image,
  List,
  ListOrdered,
  ListChecks,
  Quote,
  Minus,
  Table,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Undo,
  Redo,
  RemoveFormatting,
  FileCode,
  FileType,
} from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useState } from 'react'
import { ImagePickerDrawer } from './ImagePickerDrawer'
import { useDrawer } from '@/components/ui/drawer'

interface BlogSnippet {
  id: string
  name: string
  content: string
}

interface EditorToolbarProps {
  editor: Editor | null
  onImageUpload?: (file: File) => void
  snippets?: BlogSnippet[]
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({ editor, onImageUpload, snippets = [] }) => {
  const { openDrawer } = useDrawer()
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [, setImagePickerOpen] = useState(false)
  const [savedSelection, setSavedSelection] = useState<{ from: number; to: number } | null>(null)

  if (!editor) {
    return null
  }

  const insertSnippet = (snippetContent: string) => {
    // Insert the snippet HTML at the current cursor position
    editor.chain().focus().insertContent(snippetContent).run()
  }

  const setHeading = (level: number) => {
    if (level === 0) {
      editor.chain().focus().setParagraph().run()
    } else {
      editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 | 4 | 5 | 6 }).run()
    }
  }

  const addLink = () => {
    if (linkUrl) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run()
      setLinkUrl('')
      setLinkPopoverOpen(false)
    }
  }

  const removeLink = () => {
    editor.chain().focus().unsetLink().run()
  }

  const handleImageSelect = (url: string, alt?: string, width?: string) => {
    // Restore the saved selection if available
    if (savedSelection) {
      editor.chain()
        .focus()
        .setTextSelection(savedSelection)
        .setImage({ src: url, alt: alt || '', width: width || '100%' })
        .run()
      setSavedSelection(null)
    } else {
      // Fallback to just focusing (for backward compatibility)
      editor.chain().focus().setImage({ src: url, alt: alt || '', width: width || '100%' }).run()
    }
    setImagePickerOpen(false)
  }

  const handleImageUploadForPicker = async (file: File): Promise<string> => {
    try {
      // Use the imageUploadHandler directly for consistent behavior
      const { imageUploadHandler } = await import('./ImageUploadHandler')
      const url = await imageUploadHandler.uploadImage(file)

      // Also notify parent about upload if callback provided
      if (onImageUpload) {
        onImageUpload(file)
      }

      return url
    } catch (error) {
      console.error('Image upload failed:', error)
      throw error
    }
  }

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
  }

  return (
    <>
      <div className="sticky top-0 z-[5] bg-card border-b border-border p-2 flex flex-wrap items-center gap-1 shadow-sm">
        <Select
          value={
            editor.isActive('heading', { level: 1 })
              ? '1'
              : editor.isActive('heading', { level: 2 })
              ? '2'
              : editor.isActive('heading', { level: 3 })
              ? '3'
              : editor.isActive('heading', { level: 4 })
              ? '4'
              : '0'
          }
          onValueChange={(value) => setHeading(parseInt(value))}
        >
          <SelectTrigger className="w-32 h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Paragraph</SelectItem>
            <SelectItem value="1">Heading 1</SelectItem>
            <SelectItem value="2">Heading 2</SelectItem>
            <SelectItem value="3">Heading 3</SelectItem>
            <SelectItem value="4">Heading 4</SelectItem>
          </SelectContent>
        </Select>

        {snippets.length > 0 && (
          <>
            <Select
              value=""
              onValueChange={(value) => {
                const snippet = snippets.find(s => s.id === value)
                if (snippet) {
                  insertSnippet(snippet.content)
                }
              }}
            >
              <SelectTrigger className="w-40 h-8">
                <div className="flex items-center gap-2">
                  <FileType className="h-4 w-4" />
                  <span>Insert Snippet</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                {snippets.map((snippet) => (
                  <SelectItem key={snippet.id} value={snippet.id}>
                    {snippet.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}

        <Separator orientation="vertical" className="h-6" />

        <Button
          variant={editor.isActive('bold') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className="h-8 w-8 p-0"
          title="Bold (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive('italic') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className="h-8 w-8 p-0"
          title="Italic (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive('underline') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className="h-8 w-8 p-0"
          title="Underline (Ctrl+U)"
        >
          <Underline className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive('strike') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className="h-8 w-8 p-0"
          title="Strikethrough"
        >
          <Strikethrough className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive('code') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().toggleCode().run()}
          className="h-8 w-8 p-0"
          title="Inline Code"
        >
          <Code className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <Button
          variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className="h-8 w-8 p-0"
          title="Align Left"
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className="h-8 w-8 p-0"
          title="Align Center"
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className="h-8 w-8 p-0"
          title="Align Right"
        >
          <AlignRight className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive({ textAlign: 'justify' }) ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className="h-8 w-8 p-0"
          title="Justify"
        >
          <AlignJustify className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <Button
          variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className="h-8 w-8 p-0"
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className="h-8 w-8 p-0"
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive('taskList') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          className="h-8 w-8 p-0"
          title="Task List"
        >
          <ListChecks className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive('blockquote') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className="h-8 w-8 p-0"
          title="Blockquote"
        >
          <Quote className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive('codeBlock') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className="h-8 w-8 p-0"
          title="Code Block"
        >
          <FileCode className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={editor.isActive('link') ? 'default' : 'ghost'}
              size="sm"
              onClick={() => {
                const previousUrl = editor.getAttributes('link').href
                setLinkUrl(previousUrl || '')
              }}
              className="h-8 w-8 p-0"
              title="Add Link"
            >
              <Link2 className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 z-[10000]" align="start" side="bottom" sideOffset={8}>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="link-url" className="text-sm font-medium">
                  Link URL
                </Label>
                <Input
                  id="link-url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addLink()
                    }
                  }}
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={addLink}
                  className="flex-1"
                >
                  {editor.isActive('link') ? 'Update Link' : 'Add Link'}
                </Button>
                {editor.isActive('link') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      removeLink()
                      setLinkPopoverOpen(false)
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
        {editor.isActive('link') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={removeLink}
            className="h-8 w-8 p-0"
            title="Remove Link"
          >
            <Link2Off className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            // Save the current cursor position before opening the drawer
            const { from, to } = editor.state.selection
            setSavedSelection({ from, to })

            openDrawer({
              id: 'image-picker',
              title: 'Insert Image',
              content: (
                <ImagePickerDrawer
                  onImageSelect={handleImageSelect}
                  onImageUpload={handleImageUploadForPicker}
                />
              ),
              size: '60',
              footer: null,
            })
          }}
          className="h-8 w-8 p-0"
          title="Insert Image"
        >
          <Image className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={insertTable}
          className="h-8 w-8 p-0"
          title="Insert Table"
        >
          <Table className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="h-8 w-8 p-0"
          title="Horizontal Rule"
        >
          <Minus className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="h-8 w-8 p-0"
          title="Undo (Ctrl+Z)"
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="h-8 w-8 p-0"
          title="Redo (Ctrl+Shift+Z)"
        >
          <Redo className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          className="h-8 w-8 p-0"
          title="Remove Formatting"
        >
          <RemoveFormatting className="h-4 w-4" />
        </Button>
      </div>
    </>
  )
}